/* ──────────────────────────────────────────────────────────────
 *  useBushingTypeDialogs — Trigger dialogs on bushing type change
 *
 *  Opens the BetaTestingDialog for BYD types and
 *  C1SnubberDialog when switching to C1.
 *
 *  Triggered on: user selection, SOR upload auto-detect, part change.
 * ────────────────────────────────────────────────────────────── */

import { useEffect, useRef } from 'react';
import { useBushingStore } from '@/stores/useBushingStore';
import { useUIStore } from '@/stores/useUIStore';
import type { BushingType } from '@/types/bushing';

/** Bushing types that are in beta and trigger the notice. */
const BETA_TYPES: Set<BushingType> = new Set(['BYD EV Voided', 'BYD EV Solid']);

/**
 * Call once at app root level. Watches bushing type changes and
 * opens the appropriate dialogs:
 *  - Beta testing dialog for BYD EV Voided / BYD EV Solid
 *  - C1 Snubber dialog for C1
 */
export function useBetaDialog(): void {
  const bushingType = useBushingStore((s) => s.bushingType);
  const openDialog = useUIStore((s) => s.openDialog);
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip on initial mount — only trigger on user-driven changes
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (BETA_TYPES.has(bushingType)) {
      openDialog('betaTestingDialog');
    }

    if (bushingType === 'C1') {
      openDialog('c1SnubberDialog');
    }
  }, [bushingType, openDialog]);
}
